import { createRootRoute, createRoute, createRouter, redirect } from '@tanstack/react-router';
import { RootLayout } from './routes/layout.js';
import { LoginScreen } from './routes/login.js';
import { HomeScreen } from './routes/home.js';
import { SearchScreen } from './routes/search.js';
import { OrdersScreen } from './routes/orders.js';
import { AdminScreen } from './routes/admin.js';
import { SettingsScreen } from './routes/settings.js';
import { MeScreen } from './routes/me.js';
import { PromotionsScreen } from './routes/promotions.js';
import { InventorySearchScreen } from './routes/inventory-search.js';
import { InventoryDetailScreen } from './routes/inventory-detail.js';
import { ClientsScreen } from './routes/clients.js';
import { ClientDetailScreen } from './routes/client-detail.js';
import { CartScreen } from './routes/cart.js';
import { AuthCallbackScreen } from './routes/auth-callback.js';
import { useAuth } from './lib/auth-store.js';
import { ensureFreshSession } from './lib/cognito.js';

// Routes that don't require a session.
const UNAUTH_ROUTES = new Set(['/login', '/auth/callback']);

const rootRoute = createRootRoute({
  component: RootLayout,
  beforeLoad: async ({ location }) => {
    // Valid token, or silently renewed from the Cognito refresh token.
    const authed = await ensureFreshSession();
    if (UNAUTH_ROUTES.has(location.pathname)) {
      // Already signed in? Skip the login screen (but never short-circuit the
      // OAuth callback — it must finish the code exchange).
      if (authed && location.pathname === '/login') throw redirect({ to: '/' });
      return;
    }
    if (!authed) {
      throw redirect({ to: '/login' });
    }
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginScreen,
});

const authCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/callback',
  component: AuthCallbackScreen,
});

// Reps land on a dashboard summarizing their book of business.
const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: HomeScreen,
});

const searchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/search',
  component: SearchScreen,
});

const ordersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/orders',
  component: OrdersScreen,
});

const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin',
  beforeLoad: () => {
    const u = useAuth.getState().user;
    if (!u || u.role !== 'admin') throw redirect({ to: '/' });
  },
  component: AdminScreen,
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  beforeLoad: () => {
    const u = useAuth.getState().user;
    if (!u || u.role !== 'admin') throw redirect({ to: '/' });
  },
  component: SettingsScreen,
});

const meRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/me',
  component: MeScreen,
});

const promotionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/promotions',
  component: PromotionsScreen,
});

const inventoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/inventory',
  component: InventorySearchScreen,
});

const inventoryDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/inventory/$id',
  component: InventoryDetailScreen,
  beforeLoad: ({ params }) => {
    if (!/^\d+$/.test(params.id)) {
      throw redirect({ to: '/inventory' });
    }
  },
});

const clientsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/clients',
  // Optional ?filter=active|hold so the dashboard can deep-link a pre-filtered list.
  validateSearch: (search: Record<string, unknown>): { filter?: 'active' | 'hold' } =>
    search.filter === 'active' || search.filter === 'hold' ? { filter: search.filter } : {},
  component: ClientsScreen,
});

const clientDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/clients/$id',
  component: ClientDetailScreen,
  beforeLoad: ({ params }) => {
    if (!/^\d+$/.test(params.id)) {
      throw redirect({ to: '/clients' });
    }
  },
});

const cartRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cart',
  component: CartScreen,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  authCallbackRoute,
  homeRoute,
  inventoryRoute,
  inventoryDetailRoute,
  clientsRoute,
  clientDetailRoute,
  cartRoute,
  searchRoute,
  ordersRoute,
  adminRoute,
  settingsRoute,
  meRoute,
  promotionsRoute,
]);

export const router = createRouter({ routeTree, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
